import BluetoothIcon from '@assets/icons/bluetooth.svg';
import BluetoothOutlineIcon from '@assets/icons/bluetooth-outline.svg';
import i18n from '@/utils/i18n';
import MenuHorizontalIcon from '@assets/icons/menu-horizontal.svg';
import PrinterOutlineIcon from '@assets/icons/printer-empty.svg';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { Analytics } from '@/utils/analytics';
import {
  BORDER_RADIUS_LARGE,
  BORDER_RADIUS_SMALL,
  FONTS,
  SCREEN_PADDING_SMALL,
  shadow,
  TYPOGRAPHY,
} from '@/styles';
import { Buffer } from 'buffer';
import { Button } from '@/components/button';
import { captureException } from '@/utils/sentry';
import { CompositeScreenProps } from '@react-navigation/native';
import { createStyles } from '@/contexts/theme-context';
import { formatCurrency } from '@/utils/money';
import { FormExitConfirmation } from '@/components/form-exit-confirmation';
import { getOperationName } from 'apollo-utilities';
import { isEqual } from 'lodash';
import { isNotTagVariantPack, trackScanningEvent } from '@/utils/scanning';
import {
  ItemsContext_VariantsPackFieldsFragment,
  PlanogramFieldsFragment,
  Planograms_ListQuery,
  Planograms_ListQueryVariables,
  TagPrint_BatchDetailQuery,
  TagPrint_BatchDetailQueryVariables,
  TagPrint_BatchRequestDeliveryMutation,
  TagPrint_BatchRequestDeliveryMutationVariables,
  TagPrint_RenderBatchQuery,
  TagPrint_RenderBatchQueryVariables,
  TagPrint_RestaurantVariantPackOverridesQuery,
  TagPrint_RestaurantVariantPackOverridesQueryVariables,
  TagPrintingRenderTemplateQuery,
  TagPrintingRenderTemplateQueryVariables,
} from '@/types';
import { KeyboardAwareRecyclerList } from '@/components/recycler-list';
import { ListOption, SwipeableWithInstantClose } from '@/components/list-item';
import {
  NetworkStatus,
  useNetworkStatus,
} from '@/contexts/network-status-context';
import {
  PLANOGRAMS_QUERY,
  RENDER_TAG_PRINT_BATCH_QUERY,
  REQUEST_PRINT_TAG_BATCH_DELIVERY_MUTATION,
  TAG_PRINT_BATCH_DETAIL_QUERY,
  TAG_PRINT_BATCH_LIST_QUERY,
  TAG_PRINT_RESTAURANT_VARIANT_PACK_OVERRIDES_QUERY,
} from '@/components/tag-print-batch-queries';
import { Platform, Text, TouchableOpacity, View } from 'react-native';
import { renderZPLTemplate } from '@/utils/tag-printing';
import { RestingQuantitySelector } from '@/components/resting-quantity-selector';
import {
  ScannerType,
  ScanningEventSource,
  ScanningSearchMethod,
  ScanSound,
} from '@/components/scanner/scanner-types';
import { ScannerVariantPackListItem } from '@/components/scanner-variant-pack-list-item';
import { ScanPageContainer } from '@/components/scanner/scan-page-container';
import { StackScreenProps } from '@react-navigation/stack';
import { TAG_PRINT_BATCH_ROUTES } from '@/navigators/routes';
import { TAG_PRINTING_RENDER_TEMPLATE_QUERY } from '@/components/tag-printing-template-selection-v2';
import {
  TagPrintBatchAction,
  useTagPrintBatchReducer,
} from '@/hooks/use-tag-print-batch-reducer';
import { TagPrintBatchItemSelection } from '@/components/tag-print-batch-item-selection';
import {
  TagPrintBatchManagementActionSheet,
  TagPrintBatchManagementRoute,
} from '@/components/tag-print-batch-management-action-sheet';
import { TagPrintBatchStackParams } from '@/navigators/types';
import { TagPrintConfirmation } from '@/components/tag-print-confirmation';
import {
  TagPrintingTemplateMetadata,
  UserDefinedMetadata,
} from '@/components/tag-printing-types';
import { Type } from '@/components/toast';
import { useAdaptVariantPackToItemListOption } from '@/contexts/item-context-utils';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useBluetoothContext } from '@/contexts/bluetooth-context';
import { useComponentFocus } from '@/hooks/use-component-focus';
import { useItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useLazyQuery } from '@/hooks/use-lazy-query';
import { useMutation } from '@apollo/client';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import {
  useOfflineTagBatchPrint,
  useOfflineVisibleVariantPacks,
} from '@/hooks/use-offline-data';
import { usePlayScanSounds } from '@/hooks/use-play-scan-sounds';
import { usePrevious } from '@/hooks/use-previous';
import { useSaveTagPrintBatch } from '@/hooks/use-save-tag-print-batch';
import { useScannerSearch } from '@/hooks/use-scanner-search';
import { useToasts } from '@/contexts/toast-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

type Props = CompositeScreenProps<
  StackScreenProps<TagPrintBatchStackParams, TAG_PRINT_BATCH_ROUTES.Base>,
  StackScreenProps<
    TagPrintBatchStackParams,
    TAG_PRINT_BATCH_ROUTES.ImportTemplate
  >
>;

export const TagPrintBatchDetailScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const isOfflineModeActive = useOfflineModeActive();
  const adaptVariantPackToItemListOption =
    useAdaptVariantPackToItemListOption();
  const itemQueryVariables = useItemQueryVariables();
  const playScanSound = usePlayScanSounds();
  const { getVariantPackInfo, currencyCode } = useVariantPackInfoContext();
  const [styles, theme] = useStyles();
  const { clearSelectedDevice, selectedDevice } = useBluetoothContext();
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const { showErrorToast, showToast } = useToasts();
  const { printTagBatchUUID, selectedTemplateUUID } = route.params;
  const { error, updateTagPrintBatch } = useSaveTagPrintBatch();
  const [state, dispatch] = useTagPrintBatchReducer();

  const { networkStatus } = useNetworkStatus();
  const previousNetworkStatus = usePrevious<NetworkStatus>(networkStatus);

  const [persistOfflineBatchData, setPersistOfflineBatchData] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [showBatchManagementSheet, setShowBatchManagementSheet] =
    useState(false);
  const [enablingInstantPrint, setEnablingInstantPrint] = useState(false);
  const [isInstantPrint, setIsInstantPrint] = useState(false);
  const [showPrinterSelection, setShowPrinterSelection] = useState(false);
  const [isMakingSelection, setIsMakingSelection] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [newlyAddedVariantPackUUIDs, setNewlyAddedVariantPackUUIDs] =
    useState<Set<string> | null>(null);
  const [expandedQuantityVariantPackUUID, setExpandedQuantityVariantPackUUID] =
    useState<string | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [
    initialTagPrintBatchManagementRoute,
    setInitialTagPrintBatchManagementRoute,
  ] = useState<TagPrintBatchManagementRoute | null>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const codeRef = useRef<string>(null);
  const swipeableItemRef = useRef<SwipeableWithInstantClose>(null);

  const {
    clearResults,
    isOffDeviceSearchInProgress,
    isOnDeviceSearchInProgress,
    offDeviceSearchResults,
    onDeviceSearchResults,
    searchOffDevice,
    searchOnDevice,
  } = useScannerSearch();

  useEffect(() => {
    // block refetching batch details query if user is back online
    if (
      networkStatus === NetworkStatus.CONNECTED &&
      (previousNetworkStatus === NetworkStatus.DISCONNECTED ||
        previousNetworkStatus === NetworkStatus.UNDETERMINED)
    ) {
      setPersistOfflineBatchData(true);
    }
  }, [networkStatus, previousNetworkStatus]);

  const { data, loading } = useWrappedQuery<
    TagPrint_BatchDetailQuery,
    TagPrint_BatchDetailQueryVariables
  >(TAG_PRINT_BATCH_DETAIL_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (data.print_tag_batches_by_pk) {
        dispatch({
          payload: { batch: data.print_tag_batches_by_pk },
          type: TagPrintBatchAction.Hydrate,
        });
      }
    },
    skip: isOfflineModeActive || persistOfflineBatchData,
    variables: {
      ...itemQueryVariables,
      printTagBatchUUID,
      restaurantUUID,
    },
  });

  const getRestaurantVariantPackOverrides = useLazyQuery<
    TagPrint_RestaurantVariantPackOverridesQuery,
    TagPrint_RestaurantVariantPackOverridesQueryVariables
  >(TAG_PRINT_RESTAURANT_VARIANT_PACK_OVERRIDES_QUERY, {
    fetchPolicy: 'network-only',
  });

  const renderBatchTemplates = useLazyQuery<
    TagPrint_RenderBatchQuery,
    TagPrint_RenderBatchQueryVariables
  >(RENDER_TAG_PRINT_BATCH_QUERY, {
    fetchPolicy: 'no-cache',
  });

  const renderTemplateQuery = useLazyQuery<
    TagPrintingRenderTemplateQuery,
    TagPrintingRenderTemplateQueryVariables
  >(TAG_PRINTING_RENDER_TEMPLATE_QUERY, {
    fetchPolicy: 'no-cache',
  });

  const [requestPrintTagDelivery] = useMutation<
    TagPrint_BatchRequestDeliveryMutation,
    TagPrint_BatchRequestDeliveryMutationVariables
  >(REQUEST_PRINT_TAG_BATCH_DELIVERY_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [getOperationName(TAG_PRINT_BATCH_LIST_QUERY) as string],
    variables: {
      printTagBatchUUID,
      restaurantUUID,
      supplierUUID,
    },
  });

  const { data: offlineVisibleVariantPackData } = useOfflineVisibleVariantPacks(
    restaurantUUID,
    supplierUUID,
  );

  const {
    tagBatchPrintData: offlineTagBatchPrintData,
    updateTagPrintBatchData,
  } = useOfflineTagBatchPrint(restaurantUUID, supplierUUID);

  // batch details when offline
  const offlineBatch = offlineTagBatchPrintData?.batches?.[printTagBatchUUID];

  // offline print template for this batch
  const selectedOfflineTemplate =
    offlineTagBatchPrintData?.templates?.[state.template.uuid];

  const selectedOfflinePrinterTemplate =
    selectedOfflineTemplate?.offline_printer_text_template;

  const offlineVisibleVariantPacks = useMemo(() => {
    return (offlineVisibleVariantPackData?.visible_variant_packs || []).map(
      (vp) => vp.variant_pack!,
    );
  }, [offlineVisibleVariantPackData]);

  const batch =
    isOfflineModeActive || persistOfflineBatchData
      ? offlineBatch
      : data?.print_tag_batches_by_pk;
  const batchVariantPacks = state.variantPacks;

  const hasBatchItems = batchVariantPacks.length > 0;

  const showBatchManagementSheetCondition = isOfflineModeActive
    ? showBatchManagementSheet
    : Boolean(showBatchManagementSheet && batch);

  const { data: planogramsData } = useWrappedQuery<
    Planograms_ListQuery,
    Planograms_ListQueryVariables
  >(PLANOGRAMS_QUERY, {
    fetchPolicy: 'no-cache',
    skip: hasBatchItems || isOfflineModeActive,
    variables: {
      restaurantUUID,
      supplierUUID,
    },
  });

  const planogramsListData: ListOption<PlanogramFieldsFragment>[] = useMemo(
    () =>
      (planogramsData?.getRestaurantPlanograms ?? []).map(
        (restaurantPlanogram) => {
          const planogram = restaurantPlanogram.planogram!;
          return {
            metadata: planogram,
            title: planogram.name,
            value: planogram.uuid,
          };
        },
      ),
    [planogramsData?.getRestaurantPlanograms],
  );

  useEffect(() => {
    if (isOfflineModeActive) {
      if (batch) {
        dispatch({
          payload: { batch },
          type: TagPrintBatchAction.Hydrate,
        });
      } else if (printTagBatchUUID && selectedTemplateUUID) {
        dispatch({
          payload: {
            batch: {
              __typename: 'print_tag_batches',
              name: i18n.t('offline_batch'),
              print_tag_batch_variant_packs: [],
              print_tag_template: {
                __typename: 'print_tag_templates',
                uuid: selectedTemplateUUID,
              },
              uuid: printTagBatchUUID,
            },
          },
          type: TagPrintBatchAction.Hydrate,
        });
      }
    }
  }, [
    batch,
    dispatch,
    isOfflineModeActive,
    printTagBatchUUID,
    selectedTemplateUUID,
  ]);

  const offlinePrintTemplatesForBatch = useMemo(() => {
    if (!Boolean(selectedOfflinePrinterTemplate)) {
      return null;
    }

    const batchVariantPackUUIDsByQuantity = [];
    for (const key in state.quantityMap) {
      const quantity = state.quantityMap[key];
      for (let i = 0; i < quantity; i++) {
        batchVariantPackUUIDsByQuantity.push(key);
      }
    }

    return batchVariantPackUUIDsByQuantity
      .map((uuid) => {
        const batchVariantPack = batchVariantPacks.find(
          (bvp) => bvp.uuid === uuid,
        );
        if (!batchVariantPack) {
          return null;
        }
        const { metadata } = adaptVariantPackToItemListOption(batchVariantPack);
        const formattedPrice = batchVariantPack
          .restaurant_variant_pack_overrides[0]?.user_defined_metadata
          ?.list_prices?.retail_price_in_micros
          ? formatCurrency(
              batchVariantPack.restaurant_variant_pack_overrides[0]
                .user_defined_metadata.list_prices.retail_price_in_micros,
            )
          : undefined;

        return renderZPLTemplate(selectedOfflinePrinterTemplate!, {
          displayName: metadata?.displayName ?? '',
          externalItemId: metadata?.externalID ?? '',
          formattedPrice,
          packDisplayString: metadata?.packDisplayString ?? '',
        });
      })
      .filter(Boolean);
  }, [
    adaptVariantPackToItemListOption,
    batchVariantPacks,
    selectedOfflinePrinterTemplate,
    state.quantityMap,
  ]);

  const batchHasChanges = useMemo(() => {
    const { initialValue, ...stateRest } = state;
    return Boolean(
      (batch && !isEqual(initialValue, stateRest)) ||
        !isEqual(initialValue?.quantityMap, stateRest.quantityMap),
    );
  }, [batch, state]);

  const saveBatch = useCallback(async () => {
    const { initialValue, ...stateRest } = state;
    const hasChanges = batch && !isEqual(initialValue, stateRest);

    if (hasChanges) {
      const { name, quantityMap, variantPacks, template } = stateRest;
      const updatedBatchVariantPacks = variantPacks.map((vp) => ({
        quantity: quantityMap[vp.uuid],
        variant_pack_id: vp.uuid,
      }));

      const { data } = await updateTagPrintBatch({
        items: updatedBatchVariantPacks,
        name,
        printTagBatchUUID: batch.uuid,
        printTagTemplateUUID: template.uuid || batch.print_tag_template.uuid,
      });

      if (!data || error) {
        showErrorToast();
      } else if (
        offlineTagBatchPrintData &&
        data.upsertPrintTagBatchForRestaurant.print_tag_batch
      ) {
        await updateTagPrintBatchData({
          ...offlineTagBatchPrintData,
          batches: {
            ...offlineTagBatchPrintData.batches,
            [batch.uuid]: data.upsertPrintTagBatchForRestaurant.print_tag_batch,
          },
        });
      }
    }
  }, [
    batch,
    error,
    offlineTagBatchPrintData,
    showErrorToast,
    state,
    updateTagPrintBatch,
    updateTagPrintBatchData,
  ]);

  useComponentFocus({
    onBeforeRemove: saveBatch,
  });

  const variantPackRetailPriceInMicrosMap = useMemo(() => {
    const templateMetadata: TagPrintingTemplateMetadata | undefined =
      isOfflineModeActive
        ? selectedOfflineTemplate?.metadata
        : state.template.metadata;

    const retailPriceEnabled = templateMetadata?.features?.retail_price_enabled;

    return retailPriceEnabled
      ? batchVariantPacks.reduce<Record<string, number>>((acc, ptbvp) => {
          const variantPackUUID = ptbvp.uuid;
          const restaurantOverride = ptbvp.restaurant_variant_pack_overrides[0]
            ?.user_defined_metadata as UserDefinedMetadata | undefined;

          const retailPriceInMicros = restaurantOverride?.list_prices
            ?.retail_price_in_micros
            ? restaurantOverride.list_prices.retail_price_in_micros
            : undefined;

          if (typeof retailPriceInMicros === 'number') {
            acc[variantPackUUID] = retailPriceInMicros;
          }

          return acc;
        }, {})
      : {};
  }, [
    batchVariantPacks,
    isOfflineModeActive,
    selectedOfflineTemplate?.metadata,
    state.template.metadata,
  ]);

  const getBatchItemPriceInMicros = useCallback(
    (variantPackUUID: string) => {
      const variantPackInfo = getVariantPackInfo(variantPackUUID);

      return (
        variantPackRetailPriceInMicrosMap[variantPackUUID] ??
        variantPackInfo?.suggestedRetailPriceInMicros ??
        variantPackInfo?.manufacturerSuggestedRetailPriceInMicros
      );
    },
    [getVariantPackInfo, variantPackRetailPriceInMicrosMap],
  );

  const getVariantPackWithPriceOverrides = useCallback(
    async (variantPackUUID: string) => {
      try {
        const { data } = await getRestaurantVariantPackOverrides({
          variables: {
            ...itemQueryVariables,
            restaurantUUID,
            supplierUUID,
            variantPackUUID,
          },
        });

        return data.visible_variant_packs[0]?.variant_pack ?? null;
      } catch {
        showErrorToast();
      }

      return null;
    },
    [
      getRestaurantVariantPackOverrides,
      itemQueryVariables,
      restaurantUUID,
      showErrorToast,
      supplierUUID,
    ],
  );

  const tagCount = useMemo(
    () =>
      Object.values(state.quantityMap).reduce(
        (acc, quantity) => acc + quantity,
        0,
      ),
    [state.quantityMap],
  );

  const closeSwipeable = useCallback(() => {
    swipeableItemRef.current?.close();
    swipeableItemRef.current = null;
  }, []);

  const checkIfDeviceConnected = useCallback(
    async () => selectedDevice && (await selectedDevice.isConnected()),
    [selectedDevice],
  );

  const resetDeviceConnection = useCallback(() => {
    clearSelectedDevice();
    setIsInstantPrint(false);
    showErrorToast(i18n.t('bluetooth_device.disconnected'));
  }, [clearSelectedDevice, setIsInstantPrint, showErrorToast]);

  const onBatchPrint = useCallback(async () => {
    if (await checkIfDeviceConnected()) {
      if (isOfflineModeActive) {
        if (!selectedOfflinePrinterTemplate) {
          showErrorToast();
          return;
        }
        try {
          const printBuffer = Buffer.from(
            offlinePrintTemplatesForBatch!.join(''),
          ).toString('base64');
          await selectedDevice!.write(printBuffer, 'base64');
          setShowPrinterSelection(false);
          showToast({
            autoCloseTimeout: 3000,
            content:
              tagCount > 1
                ? i18n.t('batch_print_tags.sent_plural', { count: tagCount })
                : i18n.t('batch_print_tags.sent_singular'),
            type: Type.POSITIVE,
          });
        } catch {
          showErrorToast();
        }
      } else {
        setLoadingTemplates(true);
        try {
          const { data } = await renderBatchTemplates({
            variables: {
              printTagBatchUUID,
              restaurantUUID,
              supplierUUID,
            },
          });
          const uniqueTagCount = new Set(
            data.renderPrintTagBatchTemplatesForRestaurant.map(
              (response) => response.variant_pack_id,
            ),
          ).size;
          Analytics.track('batch_tag_print.submit_button.press', {
            deviceName: selectedDevice!.name,
            printTagBatchUUID,
            tagCount,
            uniqueTagCount,
          });
          const renderedTemplates =
            data?.renderPrintTagBatchTemplatesForRestaurant.map(
              (response) => response.rendered_template,
            );

          const printBuffer = Buffer.from(renderedTemplates.join('')).toString(
            'base64',
          );
          await selectedDevice!.write(printBuffer, 'base64');
          setShowPrinterSelection(false);

          showToast({
            autoCloseTimeout: 3000,
            content:
              tagCount > 1
                ? i18n.t('batch_print_tags.sent_plural', { count: tagCount })
                : i18n.t('batch_print_tags.sent_singular'),
            type: Type.POSITIVE,
          });
        } catch (e) {
          captureException(e);
          showErrorToast();
        }

        setLoadingTemplates(false);
      }
    } else {
      resetDeviceConnection();
    }
  }, [
    checkIfDeviceConnected,
    isOfflineModeActive,
    offlinePrintTemplatesForBatch,
    printTagBatchUUID,
    renderBatchTemplates,
    resetDeviceConnection,
    restaurantUUID,
    selectedDevice,
    selectedOfflinePrinterTemplate,
    showErrorToast,
    showToast,
    supplierUUID,
    tagCount,
  ]);

  const onInstantPrint = useCallback(
    async (variantPackUUID: string, retailPriceInMicros?: number | null) => {
      if (await checkIfDeviceConnected()) {
        if (isOfflineModeActive) {
          if (!selectedOfflinePrinterTemplate) {
            showErrorToast();
            return;
          }
          try {
            const visibleVariantPack = offlineVisibleVariantPacks.find(
              (vp) => vp.uuid === variantPackUUID,
            );
            if (visibleVariantPack) {
              const adaptedItem =
                adaptVariantPackToItemListOption(visibleVariantPack);
              const offlineRenderedTemplate = renderZPLTemplate(
                selectedOfflinePrinterTemplate!,
                {
                  displayName: adaptedItem.metadata?.displayName ?? '',
                  externalItemId: adaptedItem.metadata?.externalID ?? '',
                  formattedPrice: retailPriceInMicros
                    ? formatCurrency(retailPriceInMicros)
                    : undefined,
                  packDisplayString:
                    adaptedItem.metadata?.packDisplayString ?? '',
                },
              );
              const printBuffer = Buffer.from(offlineRenderedTemplate).toString(
                'base64',
              );
              await selectedDevice!.write(printBuffer, 'base64');
              showToast({
                autoCloseTimeout: 3000,
                content: i18n.t('batch_print_tags.sent_singular'),
                type: Type.POSITIVE,
              });
            }
          } catch {
            showErrorToast();
          }
        } else {
          setLoadingTemplates(true);
          try {
            const { data } = await renderTemplateQuery({
              variables: {
                printTagTemplateUUID: state.template.uuid,
                restaurantUUID,
                supplierUUID,
                variantPackInputs: [
                  {
                    user_input: {
                      retail_price_in_micros:
                        retailPriceInMicros ??
                        batchVariantPacks.find(
                          (vp) => vp.uuid === variantPackUUID,
                        )?.restaurant_variant_pack_overrides[0]
                          ?.user_defined_metadata?.retail_price_in_micros,
                    },
                    variant_pack_id: variantPackUUID,
                  },
                ],
              },
            });
            const printContent =
              data?.renderPrintTagTemplateForRestaurant?.find(
                (template) =>
                  template.print_tag_template_id === state.template.uuid,
              )?.rendered_template;
            if (!printContent) {
              return;
            }
            Analytics.track('batch_tag_print.instant_print.press', {
              deviceName: selectedDevice!.name,
              printTagBatchUUID,
              variantPackUUID,
            });
            const printBuffer = Buffer.from(printContent).toString('base64');

            await selectedDevice!.write(printBuffer, 'base64');

            showToast({
              autoCloseTimeout: 3000,
              content: i18n.t('batch_print_tags.sent_singular'),
              type: Type.POSITIVE,
            });
          } catch (e) {
            captureException(e);
            showErrorToast();
          }

          setLoadingTemplates(false);
        }
      } else {
        resetDeviceConnection();
      }
    },
    [
      adaptVariantPackToItemListOption,
      batchVariantPacks,
      checkIfDeviceConnected,
      isOfflineModeActive,
      offlineVisibleVariantPacks,
      printTagBatchUUID,
      renderTemplateQuery,
      resetDeviceConnection,
      restaurantUUID,
      selectedDevice,
      selectedOfflinePrinterTemplate,
      showErrorToast,
      showToast,
      state.template.uuid,
      supplierUUID,
    ],
  );

  const onAddItemToBatch = useCallback(
    async (variantPackUUID: string, quantity: number) => {
      const currentQuantity = state.quantityMap[variantPackUUID] ?? 0;

      if (currentQuantity === 0) {
        if (isOfflineModeActive) {
          const variantPack = offlineVisibleVariantPacks.find(
            (vp) => vp.uuid === variantPackUUID,
          );
          const variantPackPrices =
            offlineTagBatchPrintData?.prices?.[variantPackUUID];
          if (variantPack && variantPackPrices) {
            dispatch({
              payload: {
                variantPack: {
                  ...variantPack,
                  restaurant_variant_pack_overrides: [
                    {
                      user_defined_metadata: {
                        list_prices: {
                          retail_price_in_micros:
                            variantPackPrices.retail_price,
                        },
                      },
                      uuid: variantPackUUID,
                    },
                  ],
                },
              },
              type: TagPrintBatchAction.AddItem,
            });
          }
        } else {
          const variantPackWithPriceOverrides =
            await getVariantPackWithPriceOverrides(variantPackUUID);

          if (variantPackWithPriceOverrides) {
            dispatch({
              payload: {
                variantPack: variantPackWithPriceOverrides,
              },
              type: TagPrintBatchAction.AddItem,
            });
          }
        }
      } else if (quantity === 0 && currentQuantity === 1) {
        dispatch({
          payload: {
            variantPackUUID,
          },
          type: TagPrintBatchAction.RemoveItem,
        });
      } else {
        dispatch({
          payload: {
            quantity,
            variantPackUUID,
          },
          type: TagPrintBatchAction.UpdateQuantity,
        });
      }

      setNewlyAddedVariantPackUUIDs((currentUUIDs) => {
        if (!currentUUIDs?.has(variantPackUUID)) {
          return new Set(currentUUIDs).add(variantPackUUID);
        }
        return currentUUIDs;
      });

      // print ONLY when incrementing quantity
      if (isInstantPrint && quantity > currentQuantity) {
        if (isOfflineModeActive) {
          const offlinePices =
            offlineTagBatchPrintData?.prices?.[variantPackUUID];
          await onInstantPrint(
            variantPackUUID,
            // provide the price when offline
            offlinePices?.retail_price ??
              offlinePices?.suggested_retail_price ??
              offlinePices?.manufacturer_suggested_retail_price,
          );
        } else {
          await onInstantPrint(variantPackUUID);
        }
      }

      Analytics.track('batch_tag_print.add_item', {
        newQuantity: quantity,
        printTagBatchUUID,
        variantPackUUID,
      });
    },
    [
      dispatch,
      getVariantPackWithPriceOverrides,
      isInstantPrint,
      isOfflineModeActive,
      offlineTagBatchPrintData?.prices,
      offlineVisibleVariantPacks,
      onInstantPrint,
      printTagBatchUUID,
      state.quantityMap,
    ],
  );

  const autoAddToBatch = useCallback(
    async (variantPack: ItemsContext_VariantsPackFieldsFragment) => {
      setIsMakingSelection(false);
      const currentQuantity = state.quantityMap[variantPack.uuid] ?? 0;
      const newQuantity = currentQuantity + 1;
      await onAddItemToBatch(variantPack.uuid, newQuantity);
      playScanSound(
        newQuantity === 1
          ? ScanSound.IncrementFirst
          : ScanSound.IncrementSubsequent,
      );
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setNewlyAddedVariantPackUUIDs(null);
      }, 800);
    },
    [onAddItemToBatch, playScanSound, state.quantityMap],
  );

  const searchByCode = useCallback(
    async (code: string, scannerType: ScannerType) => {
      codeRef.current = code;
      setIsMakingSelection(true);

      const withinBatchSearchResults = searchOnDevice(
        code,
        isOfflineModeActive ? offlineVisibleVariantPacks : batchVariantPacks,
        false,
        // Tag printing must always show a chooser on a multi-match, even
        // when order-scan lowest-UOM auto-select is enabled for this chat.
        true,
      );

      if (isOfflineModeActive && withinBatchSearchResults.length === 1) {
        await autoAddToBatch(withinBatchSearchResults[0]);
        return;
      }

      // Apollo doesn't provide a mechanism to cancel an in-flight query so
      // this ensures that results from the latest query are displayed
      if (codeRef.current === code) {
        let offDeviceSearchResults = [];
        if (!isOfflineModeActive) {
          offDeviceSearchResults = await searchOffDevice(
            code,
            isNotTagVariantPack,
            // Tag printing must always show a chooser on a multi-match, even
            // when order-scan lowest-UOM auto-select is enabled for this chat.
            true,
          );
          trackScanningEvent({
            code,
            rawResults: offDeviceSearchResults.map((vp) => vp.uuid),
            restaurantUUID,
            searchMethod: ScanningSearchMethod.OffDevice,
            source: ScanningEventSource.TagBatch,
            supplierUUID,
            type: scannerType,
          });

          if (offDeviceSearchResults.length === 1) {
            await autoAddToBatch(offDeviceSearchResults[0]);
            return;
          }
        }

        if (
          withinBatchSearchResults.length === 0 &&
          offDeviceSearchResults.length === 0
        ) {
          setErrorText(i18n.t('scan.no_results'));
          setIsMakingSelection(false);
        }
      }
    },
    [
      autoAddToBatch,
      batchVariantPacks,
      isOfflineModeActive,
      offlineVisibleVariantPacks,
      restaurantUUID,
      searchOffDevice,
      searchOnDevice,
      supplierUUID,
    ],
  );

  const onClearResults = useCallback(() => {
    clearResults();
    setIsMakingSelection(false);
    setErrorText(null);
    if (newlyAddedVariantPackUUIDs && newlyAddedVariantPackUUIDs.size > 0) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setNewlyAddedVariantPackUUIDs(null);
      }, 800);
    }
  }, [clearResults, newlyAddedVariantPackUUIDs]);

  const onPrinterSelect = useCallback(() => {
    if (enablingInstantPrint) {
      setEnablingInstantPrint(false);
      setShowPrinterSelection(false);
      setIsInstantPrint(true);
    }
  }, [enablingInstantPrint]);

  const continueToPrint = useCallback(() => {
    if (isOfflineModeActive) {
      if (!Boolean(selectedOfflinePrinterTemplate)) {
        setInitialTagPrintBatchManagementRoute(
          TagPrintBatchManagementRoute.TemplateUnavailableOffline,
        );
        setShowBatchManagementSheet(true);
      } else {
        setExpandedQuantityVariantPackUUID(null);
        setShowPrinterSelection(true);
      }
    } else {
      saveBatch();
      setExpandedQuantityVariantPackUUID(null);
      setShowPrinterSelection(true);
    }
  }, [isOfflineModeActive, saveBatch, selectedOfflinePrinterTemplate]);

  const searchSections = useMemo(() => {
    const onDeviceSearchUUIDs = new Set(
      onDeviceSearchResults?.map((vp) => vp.uuid),
    );
    return [
      {
        data: [
          ...(onDeviceSearchResults ?? []),
          ...((offDeviceSearchResults ?? []).filter(
            (vp) => !onDeviceSearchUUIDs.has(vp.uuid),
          ) ?? []),
        ],
        key: i18n.t('results_from_catalog'),
      },
    ];
  }, [offDeviceSearchResults, onDeviceSearchResults]);

  const batchSections = useMemo(() => {
    return [
      {
        data: batchVariantPacks.map((bvp) => ({
          ...bvp,
          // Add value for list item highlighting
          value: bvp.uuid,
        })),
        key: null,
      },
    ];
  }, [batchVariantPacks]);

  const isSearching = isOffDeviceSearchInProgress || isOnDeviceSearchInProgress;
  const hideCameraButtons = Boolean(
    isMakingSelection &&
      offDeviceSearchResults &&
      offDeviceSearchResults?.length > 1,
  );

  const bottomAction = (
    <View style={styles.printButtonContainer}>
      {isInstantPrint && selectedDevice ? (
        <Button
          leftElement={
            <BluetoothIcon
              height={20}
              stroke={theme.sk.color.icon.ondark}
              width={20}
            />
          }
          leftElementStyle={styles.bluetoothIcon}
          onPress={() => {
            setShowPrinterSelection(true);
          }}
          style={styles.printButton}
          trackingData={{
            name: 'batch_tag_print.exit_instant_print',
            properties: {
              printTagBatchUUID,
            },
          }}
        >
          {i18n.t('instant_print.connected_to', {
            printerName: selectedDevice.name,
          })}
        </Button>
      ) : (
        <Button
          disabled={!hasBatchItems}
          onPress={continueToPrint}
          style={styles.printButton}
          trackingData={{
            name: 'batch_tag_print.printer_select',
            properties: {
              printTagBatchUUID,
            },
          }}
        >
          {i18n.t('continue_to_print')}
        </Button>
      )}
    </View>
  );

  return (
    <ScanPageContainer
      bottomAction={!isMakingSelection ? bottomAction : null}
      errorText={errorText}
      hideCameraButtons={hideCameraButtons}
      navigationHeaderProps={(scannerType) => ({
        actions: [
          {
            Icon: MenuHorizontalIcon,
            iconProps: {
              fill:
                scannerType === ScannerType.External
                  ? theme.sk.color.icon.primary
                  : theme.sk.color.icon.ondark,
            },
            onPress: () => setShowBatchManagementSheet(true),
            testID: 'tag-print-batch-actions-icon',
          },
        ],
        after:
          scannerType === ScannerType.External ? (
            <View style={styles.titleHeader}>
              <Text style={[TYPOGRAPHY.h2, FONTS.primarySemiBold]}>
                {state.name}
              </Text>
            </View>
          ) : null,
        onBackPress: () => {
          if (isOfflineModeActive && batchHasChanges) {
            setShowExitConfirmation(true);
          } else {
            navigation.goBack();
          }
        },
      })}
      onClearResults={onClearResults}
      scanInputProps={{
        scanInputClearable: true,
      }}
      searchByCode={searchByCode}
    >
      {(scannerType) => (
        <>
          {loading ? (
            <ActivityIndicator style={styles.loadingContainer} />
          ) : (
            <>
              {isMakingSelection ? (
                <View
                  style={[
                    styles.resultsContainer,
                    scannerType === ScannerType.External
                      ? styles.resultsContainerExternal
                      : null,
                  ]}
                >
                  <TagPrintBatchItemSelection
                    isSearching={isSearching}
                    onAddItemToBatch={onAddItemToBatch}
                    quantityMap={state.quantityMap}
                    sections={searchSections}
                  />
                </View>
              ) : (
                <View style={styles.content}>
                  {hasBatchItems ? (
                    <KeyboardAwareRecyclerList
                      alwaysBounceVertical={false}
                      estimatedItemSize={80}
                      extraData={{ expandedQuantityVariantPackUUID }}
                      highlightedItemValues={
                        newlyAddedVariantPackUUIDs
                          ? [...newlyAddedVariantPackUUIDs]
                          : []
                      }
                      keyboardDismissMode="on-drag"
                      keyboardShouldPersistTaps="handled"
                      listItemProps={{
                        listItemStyle: styles.batchItemContainer,
                        renderMainContent: (item) => {
                          const variantPackUUID = item.uuid;
                          const quantity = state.quantityMap[variantPackUUID];
                          const batchItemPriceInMicros =
                            getBatchItemPriceInMicros(variantPackUUID);
                          const offlinePrices =
                            offlineTagBatchPrintData?.prices?.[variantPackUUID];
                          const listOption =
                            adaptVariantPackToItemListOption(item);
                          const imageURLs =
                            listOption.metadata?.photoURLs ?? [];
                          const displayName =
                            listOption.metadata?.displayName ?? '';

                          return (
                            <TouchableOpacity
                              onPress={() => {
                                navigation.navigate(
                                  TAG_PRINT_BATCH_ROUTES.Item,
                                  {
                                    batchItemPriceInMicros,
                                    displayName,
                                    imageURLs,
                                    isInstantPrint,
                                    offlinePrices,
                                    onPrintAgain: onInstantPrint,
                                    onRetailPriceUpdate: (
                                      retailPriceInMicros: number | undefined,
                                    ) =>
                                      dispatch({
                                        payload: {
                                          retailPriceInMicros,
                                          variantPackUUID,
                                        },
                                        type: TagPrintBatchAction.UpdateRetailPrice,
                                      }),
                                    printTagBatchUUID,
                                    templateUUID:
                                      state.template.uuid ??
                                      batch?.print_tag_template.uuid,
                                    variantPackUUID,
                                  },
                                );
                              }}
                              style={styles.batchItem}
                            >
                              <ScannerVariantPackListItem
                                additionalSubtitle={
                                  typeof batchItemPriceInMicros === 'number'
                                    ? formatCurrency(
                                        batchItemPriceInMicros,
                                        currencyCode,
                                      )
                                    : i18n.t('no_price')
                                }
                                additionalSubtitleStyle={
                                  typeof batchItemPriceInMicros === 'number'
                                    ? undefined
                                    : styles.noPriceText
                                }
                                option={listOption}
                                style={styles.content}
                              />
                              <RestingQuantitySelector
                                canDecrementToZero
                                disabled={isInstantPrint}
                                disableTextInput
                                isFocused={false}
                                onChange={(previousQuantity, newQuantity) => {
                                  if (newQuantity === 0) {
                                    dispatch({
                                      payload: {
                                        variantPackUUID,
                                      },
                                      type: TagPrintBatchAction.RemoveItem,
                                    });
                                  } else {
                                    dispatch({
                                      payload: {
                                        quantity: newQuantity,
                                        variantPackUUID,
                                      },
                                      type: TagPrintBatchAction.UpdateQuantity,
                                    });
                                  }

                                  Analytics.track(
                                    'batch_tag_print.update_quantity',
                                    {
                                      newQuantity,
                                      previousQuantity,
                                      printTagBatchUUID,
                                      variantPackUUID,
                                    },
                                  );
                                }}
                                onSelect={() => {
                                  if (!isInstantPrint) {
                                    setExpandedQuantityVariantPackUUID(
                                      variantPackUUID,
                                    );
                                  }
                                }}
                                quantity={quantity}
                                resting={
                                  isInstantPrint ||
                                  expandedQuantityVariantPackUUID !==
                                    variantPackUUID
                                }
                              />
                            </TouchableOpacity>
                          );
                        },
                        swipeableProps:
                          Platform.OS === 'web'
                            ? undefined
                            : {
                                onSwipeableRightWillOpen: (_, swipeableRef) => {
                                  if (
                                    swipeableItemRef.current &&
                                    swipeableItemRef.current !== swipeableRef
                                  ) {
                                    closeSwipeable();
                                  }
                                  swipeableItemRef.current = swipeableRef;
                                },
                                renderRightActions: (item) => {
                                  const variantPackUUID = item.uuid;
                                  return (
                                    <Button
                                      onPress={() => {
                                        dispatch({
                                          payload: {
                                            variantPackUUID,
                                          },
                                          type: TagPrintBatchAction.RemoveItem,
                                        });
                                        closeSwipeable();
                                      }}
                                      style={styles.removeButton}
                                      trackingData={{
                                        name: 'batch_tag_print.remove_button',
                                        properties: {
                                          printTagBatchUUID,
                                          variantPackUUID,
                                        },
                                      }}
                                      type="danger"
                                    >
                                      {i18n.t('remove')}
                                    </Button>
                                  );
                                },
                              },
                      }}
                      onScroll={() => setExpandedQuantityVariantPackUUID(null)}
                      sections={batchSections}
                    />
                  ) : (
                    <View
                      style={[
                        styles.emptyState,
                        scannerType === ScannerType.Camera
                          ? styles.emptyStateOnCamera
                          : null,
                      ]}
                    >
                      <View style={styles.emptyStateDescription}>
                        {isInstantPrint ? (
                          <PrinterOutlineIcon
                            stroke={theme.sk.color.icon.secondary}
                          />
                        ) : (
                          <BluetoothOutlineIcon
                            stroke={theme.sk.color.icon.secondary}
                          />
                        )}
                        <Text style={[TYPOGRAPHY.medium, styles.messageText]}>
                          {i18n.t(
                            isInstantPrint
                              ? 'instant_print.empty_state'
                              : 'batch_print_tag_detail.empty_state',
                          )}
                        </Text>
                      </View>
                      {!isInstantPrint && planogramsListData.length > 0 ? (
                        <Button
                          onPress={async () => {
                            await saveBatch();
                            navigation.navigate(
                              TAG_PRINT_BATCH_ROUTES.ImportTemplate,
                              {
                                planograms: planogramsListData,
                                printTagBatchUUID,
                              },
                            );
                          }}
                          size="small"
                          style={styles.importButton}
                          textStyle={styles.importButtonText}
                          trackingData={{
                            name: 'batch_tag_print.import_template_button',
                            properties: {
                              printTagBatchUUID,
                            },
                          }}
                          type="secondary"
                        >
                          {i18n.t('import_template')}
                        </Button>
                      ) : null}
                    </View>
                  )}
                </View>
              )}
              {showPrinterSelection ? (
                <TagPrintConfirmation
                  isInstantPrint={isInstantPrint}
                  loadingTemplates={loadingTemplates}
                  onClose={() => setShowPrinterSelection(false)}
                  onPrint={onBatchPrint}
                  onPrinterSelect={onPrinterSelect}
                  setIsInstantPrint={setIsInstantPrint}
                  tagCount={tagCount}
                />
              ) : null}
            </>
          )}
          {showBatchManagementSheetCondition ? (
            <TagPrintBatchManagementActionSheet
              canUseSelectedPrintTemplateOffline={Boolean(
                selectedOfflinePrinterTemplate,
              )}
              dispatch={dispatch}
              initialRoute={initialTagPrintBatchManagementRoute}
              isInstantPrint={isInstantPrint}
              onClose={() => {
                if (initialTagPrintBatchManagementRoute) {
                  setInitialTagPrintBatchManagementRoute(null);
                }
                setShowBatchManagementSheet(false);
              }}
              onDelete={() => {
                navigation.goBack();
                showToast({
                  content: i18n.t('toast.batch_deleted'),
                  type: Type.POSITIVE,
                });
                if (offlineTagBatchPrintData) {
                  const deletedOfflineBatch =
                    offlineTagBatchPrintData.batches?.[batch!.uuid];
                  if (deletedOfflineBatch) {
                    const updatedOfflineBatches = {
                      ...offlineTagBatchPrintData.batches,
                    };
                    delete updatedOfflineBatches[batch!.uuid];

                    updateTagPrintBatchData({
                      ...offlineTagBatchPrintData,
                      batches: updatedOfflineBatches,
                    });
                  }
                }
              }}
              onRequestDeliveryOfTags={async () => {
                setShowBatchManagementSheet(false);
                try {
                  await requestPrintTagDelivery();
                  showToast({
                    content: i18n.t('toast.batch_requested', {
                      batchName: isOfflineModeActive ? state.name : batch!.name,
                    }),
                    type: Type.POSITIVE,
                  });
                } catch {
                  showErrorToast();
                }
              }}
              printTagBatchUUID={
                isOfflineModeActive ? printTagBatchUUID : batch!.uuid
              }
              setEnablingInstantPrint={setEnablingInstantPrint}
              setIsInstantPrint={setIsInstantPrint}
              setShowPrinterSelection={setShowPrinterSelection}
              state={state}
            />
          ) : null}
          {showExitConfirmation ? (
            <FormExitConfirmation
              confirmTrackingData={null}
              message={i18n.t('changes_not_saved_offline.message')}
              onConfirm={() => {
                navigation.goBack();
                setShowExitConfirmation(false);
              }}
              onSkip={() => setShowExitConfirmation(false)}
              skipTrackingData={null}
              title={i18n.t('changes_not_saved_offline.title')}
            />
          ) : null}
        </>
      )}
    </ScanPageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  batchItem: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 4,
    justifyContent: 'space-between',
  },
  batchItemContainer: {
    paddingHorizontal: SCREEN_PADDING_SMALL,
  },
  bluetoothIcon: {
    marginRight: 8,
  },
  content: {
    flex: 1,
  },
  emptyState: {
    alignItems: 'center',
    flex: 1,
    gap: 32,
    justifyContent: 'center',
  },
  emptyStateDescription: {
    alignItems: 'center',
    gap: 12,
  },
  emptyStateOnCamera: {
    paddingTop: 24,
  },
  importButton: {
    borderRadius: BORDER_RADIUS_LARGE,
    paddingHorizontal: 12,
  },
  importButtonText: {
    ...FONTS.primarySemiBold,
    fontSize: theme.fontSizes.small,
  },
  loadingContainer: {
    marginTop: 24,
  },
  messageText: {
    color: theme.sk.color.text.secondary,
    lineHeight: 24,
    paddingHorizontal: 24,
    textAlign: 'center',
  },
  noPriceText: {
    color: theme.sk.color.text.warning,
  },
  printButton: {
    borderRadius: BORDER_RADIUS_SMALL,
  },
  printButtonContainer: {
    paddingHorizontal: SCREEN_PADDING_SMALL,
    paddingTop: 16,
  },
  removeButton: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.error,
    borderRadius: 0,
    height: '100%',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  resultsContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
    marginTop: 4,
    paddingTop: 24,
  },
  resultsContainerExternal: {
    ...shadow({
      color: theme.colors.black,
      elevation: 10,
      opacity: 0.15,
      radius: BORDER_RADIUS_LARGE,
      x: 0,
      y: -4,
    }),
    borderTopLeftRadius: BORDER_RADIUS_LARGE,
    borderTopRightRadius: BORDER_RADIUS_LARGE,
  },
  titleHeader: {
    marginBottom: 16,
    marginHorizontal: SCREEN_PADDING_SMALL,
    marginTop: 8,
  },
}));
